/**
 * `/evenement/:id` — page publique d'un événement.
 *
 * PUBLIQUE au sens fort : aucune auth, aucun compte. C'est la destination des
 * liens partagés (WhatsApp, Instagram, iMessage) et des crawlers Open Graph, et
 * la seule page de l'app qu'un visiteur peut atteindre sans jamais avoir ouvert
 * la PWA. Elle doit donc se suffire à elle-même : quoi, quand, combien, où.
 *
 * `id` accepte les deux espaces d'identifiants du bridge backend : l'UUID d'un
 * événement créé par le staff, ou `agenda:<hash>` pour une entrée héritée du
 * blob agenda.json.
 *
 * Décisions produit : le prix est AFFICHÉ (aucun paiement), la capacité est
 * indicative (aucun décompte), et il n'y a délibérément aucune interaction
 * client — pas d'inscription, pas de « Ça m'intéresse », pas de compteur.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBranding } from '@alterrego/module-branding';
import { MaskIcon } from '../components/Icon';
import {
  eventShareUrl,
  fetchPublicEvent,
  formatDateFull,
  formatPrice,
  formatTimeRange,
  type PublicEvent,
  type PublicEventTenant,
} from '../utils/events';
import { publicTypeConfig } from '../utils/eventTypes';
import './EventPage.css';

export function EventPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [tenant, setTenant] = useState<PublicEventTenant | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'missing'>('loading');
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setState('loading');
    fetchPublicEvent(id).then((res) => {
      if (!res) {
        setState('missing');
        return;
      }
      setEvent(res.event);
      setTenant(res.tenant);
      setState('ok');
    });
  }, [id]);

  // Le toast de partage s'efface seul : c'est une confirmation, pas un état.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  // Adresse : le branding du tenant fait foi (il est figé au build et toujours
  // complet), la row `establishments` du backend ne sert que de repli.
  const est = branding.establishment ?? {};
  const place = {
    name: est.name ?? tenant?.name ?? branding.displayName,
    address: est.address ?? tenant?.address ?? '',
    postalCode: est.postalCode ?? tenant?.postalCode ?? '',
    city: est.city ?? tenant?.city ?? '',
    latitude: est.latitude ?? tenant?.latitude ?? null,
    longitude: est.longitude ?? tenant?.longitude ?? null,
  };
  const mapsUrl = useMemo(() => {
    const q = encodeURIComponent(place.name);
    return place.latitude != null && place.longitude != null
      ? `https://maps.apple.com/?q=${q}&ll=${place.latitude},${place.longitude}`
      : `https://maps.apple.com/?q=${q}`;
  }, [place.name, place.latitude, place.longitude]);

  if (state === 'loading') {
    return (
      <div className="evp">
        <p className="evp-status">Chargement…</p>
      </div>
    );
  }

  if (state === 'missing' || !event) {
    return (
      <div className="evp">
        <div className="evp-missing">
          <MaskIcon name="calendar" size={30} color="var(--color-text-secondary)" />
          <p>Cet événement n'est plus disponible.</p>
          <button className="evp-btn evp-btn-primary" onClick={() => navigate('/agenda')}>
            Voir l'agenda
          </button>
        </div>
      </div>
    );
  }

  const cfg = publicTypeConfig(event.type);
  const timeRange = formatTimeRange(event.startTime, event.endTime);

  /**
   * Partage. L'URL pointe sur le backend et non sur `window.location.href` :
   * en natif l'app tourne sur `capacitor://localhost`, dont l'URL est
   * inexploitable hors du téléphone (le bug déjà corrigé sur la home et /lieu).
   * Le lien backend porte en plus les métas Open Graph de l'événement.
   *
   * Web Share API quand elle existe (feuille de partage iOS/Android native),
   * presse-papier sinon (desktop, preview). Un partage annulé par
   * l'utilisateur lève AbortError : ce n'est pas une erreur, on ne dit rien.
   */
  const handleShare = async () => {
    const url = eventShareUrl(event.id);
    const text = `${event.title} — ${formatDateFull(event.date)} chez ${place.name}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: event.title, text, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setToast('Lien copié !');
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      // Clipboard refusé (contexte non sécurisé, permission) : on affiche l'URL
      // plutôt que d'échouer en silence, l'utilisateur peut la copier à la main.
      setToast(url);
    }
  };

  return (
    <div className="evp">
      {/* HERO — cover pleine largeur, dégradé pour que le titre reste lisible
          quelle que soit la photo. Sans cover : aplat de la couleur du type. */}
      <div
        className="evp-hero"
        style={
          event.coverImageUrl
            ? { backgroundImage: `url(${event.coverImageUrl})` }
            : { background: cfg.color }
        }
      >
        <div className="evp-hero-scrim" />
        <button className="evp-back" onClick={() => navigate(-1)} aria-label="Retour">
          <MaskIcon name="chevron-left" size={18} color="#fff" />
        </button>
        <button className="evp-share" onClick={handleShare} aria-label="Partager l'événement">
          <MaskIcon name="arrow-up-from-bracket" size={18} color="#fff" />
        </button>
      </div>

      <div className="evp-content">
        <span className="evp-type" style={{ background: cfg.color }}>{cfg.label}</span>
        <h1 className="evp-title">{event.title}</h1>

        <div className="evp-when">
          <MaskIcon name="calendar" size={16} color="var(--color-primary)" />
          <div>
            <span className="evp-when-date">
              {formatDateFull(event.date)}
              {event.dateEnd ? ` → ${formatDateFull(event.dateEnd)}` : ''}
            </span>
            {timeRange && <span className="evp-when-time">{timeRange}</span>}
          </div>
        </div>

        {/* Prix et places : affichage seul. Le prix est TOUJOURS montré (« Entrée
            libre » quand il n'y en a pas) — l'absence d'information sur le tarif
            est la première raison de ne pas venir. */}
        <div className="evp-facts">
          <div className="evp-fact">
            <span className="evp-fact-label">Tarif</span>
            <span className="evp-fact-value">{formatPrice(event.price)}</span>
          </div>
          {event.capacity != null && (
            <div className="evp-fact">
              <span className="evp-fact-label">Places</span>
              <span className="evp-fact-value">Places limitées : {event.capacity}</span>
            </div>
          )}
        </div>

        {event.description && (
          <div className="evp-section">
            <h2>À propos</h2>
            {/* Texte brut : on respecte les retours à la ligne saisis par le
                staff sans interpréter de markdown (aucun HTML injecté). */}
            <p className="evp-description">{event.description}</p>
          </div>
        )}

        {/* OÙ — l'adresse et l'itinéraire. Les moyens d'accès détaillés (métro,
            vélo, parkings, boussole) vivent déjà sur /lieu : on y renvoie plutôt
            que de les dupliquer et de les laisser diverger. */}
        <div className="evp-section">
          <h2>Où</h2>
          <div className="evp-place">
            <MaskIcon name="location-dot" size={22} color="var(--color-primary)" />
            <div className="evp-place-lines">
              <span className="evp-place-name">{place.name}</span>
              {place.address && (
                <span className="evp-place-addr">
                  {place.address}
                  {place.postalCode || place.city ? `, ${place.postalCode} ${place.city}`.trim() : ''}
                </span>
              )}
            </div>
          </div>
          <div className="evp-place-actions">
            <a className="evp-btn" href={mapsUrl} target="_blank" rel="noreferrer">
              <MaskIcon name="location-arrow" size={15} color="var(--color-primary)" />
              Itinéraire
            </a>
            <button className="evp-btn" onClick={() => navigate('/lieu')}>
              <MaskIcon name="store" size={15} color="var(--color-primary)" />
              Comment venir
            </button>
          </div>
        </div>

        {/* Partage — doublé volontairement : l'icône du hero pour qui connaît le
            geste, ce bouton explicite en fin de page pour qui vient de finir de
            lire et décide d'en parler. C'est LE seul appel à l'action de la
            page (aucune inscription dans cette phase). */}
        <button className="evp-share-wide" onClick={handleShare}>
          <MaskIcon name="share-nodes" size={16} color="#fff" />
          Partager l'événement
        </button>
      </div>

      {toast && <div className="evp-toast">{toast}</div>}
    </div>
  );
}
